/**
 * CommissionStatementsCard — the statement emails (Oct 19, 2026), one card per
 * kind: kind="commission" on Finance → Commissions → Paid, kind="spiff" on
 * Finance → Spiffs, kind="period" (the monthly / quarterly / annual
 * statements sent on the 1st) on the Paid tab under the commission card.
 *
 * Every commission payout recorded in commission_payments (paid in Books, or
 * Mark paid / Pay All / RS pool / true-ups on this board) queues an email for
 * that person and pay day; so does every SPIFF marked paid (the Books bill
 * paid, or Mark paid on the Spiffs tab). After ten quiet minutes the database
 * sends ONE statement per person per day per kind through the
 * commission-paid-email edge function: for commissions job · revenue · rate ·
 * commission · deductions · paid; for SPIFFs the SPIFF, the week it was won and
 * the amount (an ASM's override broken down by rep); both say how it was paid.
 * This card is where finance sees that it happened:
 *   • the mode (test → everything goes to Greg; on → each person, cc Shannon; off)
 *   • the last 30 days of statements with their status
 *   • View (renders the statement, nothing is sent) and Resend.
 * Nothing here writes the table directly — Resend is the requeue RPC (finance /
 * admin only, server-checked), and the cron sends it within two minutes.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { invokeErrorMessage } from "../../lib/edgeError";
import { useRealtimeTable } from "../../lib/useRealtimeTable";

type StatementKind = "commission" | "spiff" | "period";
type RowKind = "commission" | "spiff" | "month" | "quarter" | "year";

interface StatementRow {
  id: string;
  member_id: string;
  pay_day: string;
  kind: RowKind;
  period_start: string | null;
  status: "queued" | "sending" | "sent" | "failed" | "skipped";
  line_ids: string[];
  spiff_ids: string[];
  last_activity_at: string;
  attempts: number;
  mode: string | null;
  recipient_email: string | null;
  subject: string | null;
  total: number | string | null;
  error_detail: string | null;
  skip_reason: string | null;
  sent_at: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-10-16" → "Oct 16" (date-only: never through new Date, no timezone slip). */
function dayLabel(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : iso;
}
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** "October 2026 · monthly", "Q4 2026 · quarterly", "2026 · annual". */
function periodLabel(r: StatementRow): string {
  const start = r.period_start ?? r.pay_day;
  const y = start.slice(0, 4), m = Number(start.slice(5, 7));
  if (r.kind === "month") return `${MONTHS_FULL[m - 1]} ${y} · monthly`;
  if (r.kind === "quarter") return `Q${Math.floor((m - 1) / 3) + 1} ${y} · quarterly`;
  return `${y} · annual`;
}
const isPeriodRow = (r: StatementRow) => r.kind === "month" || r.kind === "quarter" || r.kind === "year";
/** What the statement covers, for the row and the dialogs. */
const coverLabel = (r: StatementRow) => (isPeriodRow(r) ? periodLabel(r) : dayLabel(r.pay_day));

function timeLabel(ts: string | null): string {
  if (!ts) return "";
  return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function StatusChip({ r }: { r: StatementRow }) {
  if (r.status === "sent") {
    const test = r.mode === "test";
    return (
      <span
        title={`Sent ${timeLabel(r.sent_at)} to ${r.recipient_email ?? "?"}`}
        className={"text-[10px] font-semibold px-1.5 py-0.5 rounded " + (test ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700")}
      >
        {test ? "✓ sent to test address" : "✓ sent"} · {timeLabel(r.sent_at)}
      </span>
    );
  }
  if (r.status === "queued") {
    const due = new Date(r.last_activity_at).getTime() + 10 * 60_000;
    const mins = Math.max(0, Math.ceil((due - Date.now()) / 60_000));
    return (
      <span title="Waits ten quiet minutes so one pay run becomes one email" className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">
        queued · {mins > 0 ? `sends in ~${mins + 2} min` : "sends within 2 min"}
      </span>
    );
  }
  if (r.status === "sending") return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">sending…</span>;
  if (r.status === "failed") {
    return (
      <span title={r.error_detail ?? ""} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 cursor-help">
        ⚠ failed{r.attempts < 3 ? " · retries hourly" : ""}
      </span>
    );
  }
  return (
    <span title={r.skip_reason ?? ""} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 cursor-help">
      not sent
    </span>
  );
}

const KIND_COPY: Record<StatementKind, { title: string; intro: string; empty: string; unit: string }> = {
  commission: {
    title: "📧 Commission statements",
    intro: "When a commission is paid (in Books, or marked paid here), each person gets one email per pay day listing every job: revenue, rate, commission, deductions and what was paid. Statements wait ten quiet minutes so one pay run becomes one email.",
    empty: "No statements in the last 30 days. The next commission paid starts them.",
    unit: "line",
  },
  spiff: {
    title: "📧 SPIFF statements",
    intro: "When a SPIFF is paid (its Books bill paid, or marked paid here), each person gets one email per pay day listing every SPIFF: the SPIFF, the week it was won and the amount, with an ASM's override broken down by rep. Statements wait ten quiet minutes so one pay run becomes one email.",
    empty: "No SPIFF statements in the last 30 days. The next SPIFF paid starts them.",
    unit: "SPIFF",
  },
  period: {
    title: "📧 Monthly, quarterly & annual statements",
    intro: "On the 1st at 8 AM ET, everyone paid the month before gets that month's statement: every job and SPIFF paid in it, totals by kind, year to date. When the month closes a quarter or the year, the quarterly and annual statements follow as separate emails. Each person also sees all of them on My Pay.",
    empty: "None sent yet. The first go out on the 1st, for the month before.",
    unit: "item",
  },
};

export default function CommissionStatementsCard({ kind = "commission" }: { kind?: StatementKind }) {
  const copy = KIND_COPY[kind];
  const [rows, setRows] = useState<StatementRow[] | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<{ mode: string; testTo: string; cc: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [view, setView] = useState<{ title: string; html: string | null; error: string | null } | null>(null);

  useRealtimeTable("commission_payment_emails", () => setReloadKey((k) => k + 1));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // period rows carry the period's last day as pay_day; keep a quarter of them in view
      const since = new Date(Date.now() - (kind === "period" ? 100 : 30) * 86_400_000).toISOString().slice(0, 10);
      const kinds: RowKind[] = kind === "period" ? ["month", "quarter", "year"] : [kind];
      const [q, s] = await Promise.all([
        supabase
          .from("commission_payment_emails")
          .select("id, member_id, pay_day, kind, period_start, status, line_ids, spiff_ids, last_activity_at, attempts, mode, recipient_email, subject, total, error_detail, skip_reason, sent_at")
          .in("kind", kinds)
          .gte("pay_day", since)
          .order("pay_day", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(200),
        supabase
          .from("finance_settings")
          .select("key, value_jsonb")
          .in("key", ["commission_paid_email_mode", "commission_paid_email_test_to", "commission_paid_email_cc"]),
      ]);
      if (cancelled) return;
      if (q.error) { setErr(formatSupabaseError(q.error)); setRows([]); return; }
      const list = (q.data ?? []) as StatementRow[];
      setRows(list);
      setErr(null);
      if (!s.error) {
        const get = (k: string) => {
          const v = (s.data ?? []).find((r: { key: string }) => r.key === k)?.value_jsonb;
          return typeof v === "string" ? v : v == null ? "" : String(v);
        };
        setMode({ mode: get("commission_paid_email_mode") || "on", testTo: get("commission_paid_email_test_to"), cc: get("commission_paid_email_cc") });
      }
      const ids = [...new Set(list.map((r) => r.member_id))];
      if (ids.length) {
        const { data } = await supabase.from("team_roster").select("id, full_name").in("id", ids);
        if (!cancelled) {
          const map: Record<string, string> = {};
          for (const p of (data ?? []) as Array<{ id: string; full_name: string | null }>) map[p.id] = p.full_name ?? "";
          setNames(map);
        }
      }
    })().catch((e: unknown) => { if (!cancelled) { setErr(formatSupabaseError(e)); setRows([]); } });
    return () => { cancelled = true; };
  }, [reloadKey, kind]);

  // The newest pay day's statements, for the one-line summary in the header.
  const summary = useMemo(() => {
    if (!rows || !rows.length) return null;
    const day = rows[0].pay_day;
    const ofDay = rows.filter((r) => r.pay_day === day);
    const sent = ofDay.filter((r) => r.status === "sent").length;
    const waiting = ofDay.filter((r) => r.status === "queued" || r.status === "sending").length;
    const failed = rows.filter((r) => r.status === "failed").length;
    return { day, total: ofDay.length, sent, waiting, failed };
  }, [rows]);

  const resend = useCallback(async (r: StatementRow) => {
    const who = names[r.member_id] ?? "this person";
    const target = mode?.mode === "test" ? `the test address (${mode.testTo})` : who;
    if (!window.confirm(`Send ${who}'s ${coverLabel(r)} statement again? It goes to ${target} within about two minutes.`)) return;
    setBusy(r.id);
    setFlash(null);
    const { error } = await supabase.rpc("commission_paid_email_requeue", { p_id: r.id });
    setBusy(null);
    if (error) { setFlash(`Couldn't resend: ${formatSupabaseError(error)}`); return; }
    setFlash(`Queued — ${who}'s statement goes out within about two minutes.`);
    setReloadKey((k) => k + 1);
  }, [names, mode]);

  const openView = useCallback(async (r: StatementRow) => {
    const title = `${names[r.member_id] ?? "Statement"} · ${coverLabel(r)}`;
    setView({ title, html: null, error: null });
    const { data, error } = await supabase.functions.invoke("commission-paid-email", { body: { queue_id: r.id, dry: true } });
    if (error) { setView({ title, html: null, error: await invokeErrorMessage(error, "Couldn't render the statement") }); return; }
    const d = data as { html?: string; reason?: string } | null;
    setView({ title, html: d?.html ?? null, error: d?.html ? null : (d?.reason ?? "Nothing to show") });
  }, [names]);

  const modeChip = mode && (
    mode.mode === "test" ? (
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" title="Every statement (commission and SPIFF) goes to the test address instead of the person. Flip commission_paid_email_mode to 'on' to send them to the team.">
        Test mode · all go to {mode.testTo || "the test address"}
      </span>
    ) : mode.mode === "off" ? (
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Off · nothing is sent</span>
    ) : (
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
        On · each person gets theirs{mode.cc ? `, cc ${mode.cc}` : ""}
      </span>
    )
  );

  return (
    <div className="bg-white rounded-xl shadow">
      <details>
        <summary className="px-4 py-3 cursor-pointer select-none flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-bardin-ink">{copy.title}</span>
          {modeChip}
          {summary && (
            <span className="text-[11px] text-slate-500">
              {kind === "period" ? "Latest" : dayLabel(summary.day)}: {summary.sent} of {summary.total} sent
              {summary.waiting ? ` · ${summary.waiting} waiting` : ""}
            </span>
          )}
          {summary && summary.failed > 0 && (
            <span className="text-[11px] font-semibold text-rose-600">⚠ {summary.failed} failed</span>
          )}
        </summary>
        <div className="px-4 pb-4 space-y-2">
          <p className="text-[11px] text-slate-500">{copy.intro}</p>
          {flash && <div className="text-xs rounded-lg px-3 py-2 bg-slate-50 text-slate-700">{flash}</div>}
          {err ? (
            <div className="text-xs rounded-lg px-3 py-2 bg-red-50 text-red-700">Couldn't load the statements — {err}</div>
          ) : rows === null ? (
            <div className="text-xs text-slate-400 py-2">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-slate-400 py-2">{copy.empty}</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((r) => {
                const n = (r.kind === "spiff" ? r.spiff_ids : r.line_ids)?.length ?? 0;
                return (
                <div key={r.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <div className="min-w-[9rem] flex-1">
                    <div className="text-sm font-medium text-bardin-ink">{names[r.member_id] ?? "Former team member"}</div>
                    <div className="text-[11px] text-slate-500">
                      {isPeriodRow(r)
                        ? <>{periodLabel(r)}{r.total != null ? ` · ${fmtUSD(Number(r.total))}` : ""}</>
                        : <>Paid {dayLabel(r.pay_day)} · {n} {copy.unit}{n === 1 ? "" : "s"}{r.total != null ? ` · ${fmtUSD(Number(r.total))}` : ""}</>}
                    </div>
                  </div>
                  <StatusChip r={r} />
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => openView(r)}
                      className="text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      View
                    </button>
                    {(r.status === "sent" || r.status === "failed" || r.status === "skipped") && (
                      <button
                        disabled={busy === r.id}
                        onClick={() => resend(r)}
                        className="text-[11px] px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {busy === r.id ? "…" : "Resend"}
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </details>

      {view && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" onClick={() => setView(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-bardin-ink">{view.title}</h3>
              <button onClick={() => setView(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Close">×</button>
            </div>
            {view.error ? (
              <div className="p-4 text-sm text-red-700">{view.error}</div>
            ) : view.html === null ? (
              <div className="p-6 text-sm text-slate-500">Rendering…</div>
            ) : (
              <iframe title={view.title} sandbox="" srcDoc={view.html} className="w-full h-[70vh] rounded-b-xl" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
